import styled from 'styled-components';

import { mediaUp, rem } from 'approot/shared/style';
import {
  azure1,
  grey5,
  grey7,
  grey8,
  grey9,
  grey10,
} from 'approot/shared/styles/colours.style';
import {
  spacing4,
  spacing8,
  spacing12,
  spacing16,
  spacing24,
} from 'approot/shared/styles/spacing.style';
import { DropdownSecondaryKebabToggle } from 'approot/shared/dropdown/dropdown-kebab.style';
import { DropdownToggle } from 'approot/shared/dropdown/dropdown.style';
import {
  ActionsMenu,
  ActionsMenuContainer,
} from 'approot/shared/styles/actions-menu.style';
import { RoundIconButtonStyled } from 'approot/shared/round-icon-button/round-icon-button.style';
import { Ellipsis } from 'approot/shared/styles/ellipsis.style';
import {
  font14,
  font16,
  font18,
  fontWeightMedium,
  purpleLinkBase,
  PStyled,
  SmallStyled,
  Heading16,
  AnchorStyled,
} from 'approot/shared/styles/typography.style';
import Switch from 'approot/shared/switch/switch';
import { borderRadius16 } from 'approot/shared/styles/borders.style';
import {
  baseFlexCss,
  flexCentreAlignCss,
  flexColumnCss,
} from 'approot/shared/styles/flex.style';
import { Box } from 'approot/shared/design-system/box/box.style';

type TeachingLearningSequenceIconProps = {
  $size?: number;
};

export const TeachingLearningSequenceIcon = styled.div<TeachingLearningSequenceIconProps>`
  ${flexCentreAlignCss}
  margin-right: ${rem(spacing16)};
  background-color: ${azure1};
  border-radius: 50%;
  flex-shrink: 0;

  width: ${({ $size = 32 }) => rem($size)};
  height: ${({ $size = 32 }) => rem($size)};

  svg {
    fill: none;
  }
`;

export const TeachingLearningSequenceListKebabWrapper = styled.div`
  ${DropdownSecondaryKebabToggle} {
    height: ${rem(32)};
    padding: ${rem(spacing4)} ${rem(spacing12)};
  }
`;

export const TeachingLearningSequencePillToggle = styled(DropdownToggle).attrs({
  $isTransparent: false,
})`
  min-height: ${rem(32)};
  margin-right: ${rem(spacing16)};
  width: auto;
`;

export const TeachingLearningSequenceListDropdownWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${rem(spacing8)};
`;

export const TeachingLearningSequenceListActionsWrapper = styled.div`
  ${ActionsMenuContainer} {
    margin-bottom: 0;

    ${RoundIconButtonStyled} {
      width: ${rem(32)};
      height: ${rem(32)};
    }
  }
`;

export const TeachingLearningSequenceListEllipsis = styled(Ellipsis).attrs({
  as: 'span',
})`
  line-height: ${rem(22)};
  text-align: left;
`;

export const TeachingLearningSequenceSwitch = styled(Switch)`
  margin-right: ${rem(spacing16)};
`;

export const TeachingLearningSequenceSwitchLabel = styled(PStyled).attrs({
  colour: grey9,
})`
  margin: 0;
`;

export const TeachingLearningSequenceSwitchWrapper = styled.div`
  ${baseFlexCss}
  margin-bottom: ${rem(spacing24)};

  & > svg {
    margin-left: ${rem(spacing8)};
  }
`;

export const TeachingLearningSequencePillWrapper = styled(Box)``;

type TeachingLearningSequenceContainerProps = {
  $marginBottom?: number;
};

export const TeachingLearningSequenceContainer = styled.div<TeachingLearningSequenceContainerProps>`
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius16)};
  width: 100%;
  max-width: ${rem(552)};

  margin-bottom: ${({ $marginBottom }) =>
    $marginBottom ? rem($marginBottom) : 0};
`;

export const TeachingLearningSequenceViewContainer = styled.div`
  width: 100%;
  max-width: ${rem(552)};
  margin-bottom: ${rem(spacing16)};
`;

export const TeachingLearningSequenceHeader = styled.div`
  ${baseFlexCss}
  border-bottom: 1px solid ${grey5};
  padding: ${rem(spacing16)};
`;

export const TeachingLearningSequenceQuestionHeader = styled(
  TeachingLearningSequenceHeader
)`
  justify-content: space-between;
`;

export const TeachingLearningSequenceQuestionHeaderLeft = styled.div`
  ${baseFlexCss}
`;

export const TeachingLearningSequenceViewHeader = styled.div`
  ${baseFlexCss}
  margin-bottom: ${rem(spacing24)};
  padding: ${rem(spacing16)} ${rem(spacing16)} 0 0;
`;

export const TeachingLearningSequenceContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${rem(spacing16)};
`;

export const TeachingLearningSequenceViewContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const TeachingLearningSequenceTitle = styled(PStyled).attrs({
  colour: grey9,
})`
  ${font18}

  font-weight: ${fontWeightMedium};
  margin: 0;
`;

export const TeachingLearningSequenceViewTitle = styled(Heading16).attrs({
  colour: grey9,
  as: 'h4',
})`
  font-weight: ${fontWeightMedium};
`;

export const TeachingLearningSequenceLabel = styled(PStyled).attrs({
  colour: grey9,
  marginBottom: spacing4,
})``;

export const TeachingLearningSequenceEmptySubHeading = styled(PStyled).attrs({
  colour: grey9,
  marginBottom: spacing8,
})`
  font-weight: ${fontWeightMedium};
`;

export const TeachingLearningSequenceEmptyText = styled(PStyled).attrs({
  colour: grey7,
  marginBottom: spacing24,
})``;

type TeachingLearningSequenceRichtextProps = {
  $isSmall?: boolean;
};

export const TeachingLearningSequenceRichtext = styled.div<TeachingLearningSequenceRichtextProps>`
  p,
  ul,
  ol {
    margin-bottom: ${rem(spacing24)};
    ${({ $isSmall }) => ($isSmall ? font14 : font16)};
  }

  a {
    ${purpleLinkBase}
    ${({ $isSmall }) => ($isSmall ? font14 : font16)};
  }
`;

export const TeachingLearningSequenceQuestionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: ${rem(552)};
`;

export const TeachingLearningSequenceArrow = styled.div`
  svg {
    /* overrides svg styles in dropdown-pill.tsx */
    fill: none !important;

    stroke: ${grey10};
    margin: 0 ${rem(spacing4)};

    ${mediaUp.sm`
      margin: 0 ${rem(spacing8)};
    `}
  }
`;

export const TeachingLearningSequenceActionsMenu = styled(ActionsMenu)`
  margin-left: ${rem(spacing8)};

  ${mediaUp.sm`
    margin-left: ${rem(spacing16)};
  `}
`;

export const TeachingLearningSequenceQuestionDuration = styled(SmallStyled)`
  margin: 0 ${rem(spacing4)} 0 0;
  color: ${grey8};
`;

export const TeachingLearningSequenceQuestionDetails = styled.div`
  ${flexColumnCss}
  align-items: flex-start;
  margin-left: ${rem(spacing16)};
`;

export const TeachingLearningSequenceLink = styled(AnchorStyled)`
  ${font14}
  color: ${grey8};
`;
